import { Alert, Button } from "flowbite-react";
import axiosInstance from "../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { HiCreditCard, HiReceipt } from "react-icons/hi";
import { useLocation, useNavigate } from "react-router-dom";
import { useApplication } from "../contexts/ApplicationContext";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";
import { HiBolt, HiReceiptPercent } from "react-icons/hi2";

const CURRENCY_FORMATTER = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return CURRENCY_FORMATTER.format(parsed);
};

const intervalLabel = (months) => {
  if (!months || months <= 0) return "-";
  if (months === 1) return "Mensal";
  if (months === 3) return "Trimestral";
  if (months === 12) return "Anual";
  return `${months} meses`;
};

const intervalDescriptor = (months) => {
  if (months === 1) return "mensal";
  if (months === 3) return "trimestral";
  if (months === 12) return "anual";
  if (months > 1) return `${months} meses`;
  return "-";
};

const installmentTextForCycle = (cycle) => {
  if (!cycle) return "";
  const installments = cycle.intervalMonths;
  const base =
    cycle.billingType && cycle.billingType.toLowerCase().includes("credit")
      ? "Cartão de crédito"
      : cycle.billingType || "Cartão de crédito";
  if (installments > 1) {
    const installmentValue = cycle.price / installments;
    return `${installments}x de ${formatCurrency(installmentValue)}`;
  }
  return `À vista`;
};

const PAYMENT_METHODS = [
  {
    value: "CREDIT_CARD",
    title: "Cartão de crédito",
    description: "Parcelamos o valor pelo número de meses escolhidos.",
    icon: HiCreditCard,
  },
  {
    value: "PIX",
    title: "PIX / QR Code",
    description: "Pague rapidamente escaneando o QR Code ou copiando o link.",
    icon: HiBolt,
  },
  {
    value: "BOLETO",
    title: "Boleto bancário",
    description:
      "Receba o boleto por e-mail e pague no banco ou internet banking.",
    icon: HiReceiptPercent,
  },
];

const onlyDigits = (value = "") => value.replace(/\D+/g, "");

const formatCpfCnpj = (value = "") => {
  const digits = onlyDigits(value);
  if (digits.length <= 11) {
    const padded = digits.padEnd(11, "_");
    return padded
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2")
      .replace(/_/g, "")
      .trim();
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .slice(0, 18);
};

const validateCPF = (value = "") => {
  const digits = onlyDigits(value);
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
  const calc = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i += 1) {
      sum += Number(digits[i]) * (len + 1 - i);
    }
    const result = (sum * 10) % 11;
    return result === 10 ? 0 : result;
  };
  const first = calc(9);
  const second = calc(10);
  return first === Number(digits[9]) && second === Number(digits[10]);
};

const validateCNPJ = (value = "") => {
  const digits = onlyDigits(value);
  if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return false;
  const weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const calc = (length) => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) {
      sum += Number(digits[i]) * weights[length + 1 - i];
    }
    const result = sum % 11;
    return result < 2 ? 0 : 11 - result;
  };
  const first = calc(12);
  const second = calc(13);
  return first === Number(digits[12]) && second === Number(digits[13]);
};

const isValidDocument = (value = "") => {
  const digits = onlyDigits(value);
  if (digits.length === 11) return validateCPF(digits);
  if (digits.length === 14) return validateCNPJ(digits);
  return false;
};

const SubscriptionPaymentPage = () => {
  const { user } = useApplication();
  const company = user?.Company || {};
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const planIdFromQueryRaw = searchParams.get("planId");
  const cycleIdFromQueryRaw = searchParams.get("cycleId");
  const planIdFromQuery = planIdFromQueryRaw
    ? Number(planIdFromQueryRaw)
    : undefined;
  const cycleIdFromQuery = cycleIdFromQueryRaw
    ? Number(cycleIdFromQueryRaw)
    : undefined;
  const planIdFromState = location.state?.planId;
  const cycleIdFromState = location.state?.cycleId;
  const planId = Number.isFinite(planIdFromQuery)
    ? planIdFromQuery
    : planIdFromState;
  const cycleId = Number.isFinite(cycleIdFromQuery)
    ? cycleIdFromQuery
    : cycleIdFromState;

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(
    location.state?.paymentMethod || "CREDIT_CARD"
  );
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    document: "",
    phone: "",
    cardNumber: "",
    cardHolderName: "",
    cardExpiryMonth: "",
    cardExpiryYear: "",
    cardCvv: "",
  });
  const [installments, setInstallments] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [documentTouched, setDocumentTouched] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      fullName: prev.fullName || user?.name || company?.responsibleName || "",
      email: prev.email || user?.email || company?.email || "",
      document:
        prev.document || company?.document || company?.responsibleCPF || "",
      phone: prev.phone || company?.phoneNumber || "",
    }));
  }, [user, company]);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const { data } = await axiosInstance.get("/assinatura");
        if (!active) return;
        setSubscriptionData(data?.data ?? null);
        setFetchError(null);
      } catch (error) {
        if (!active) return;
        console.error("Erro ao carregar planos:", error);
        setFetchError("Não foi possível carregar os planos disponíveis.");
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };
    fetch();
    return () => {
      active = false;
    };
  }, []);

  const availablePlans = subscriptionData?.availablePlans || [];
  const plan = useMemo(() => {
    if (!availablePlans.length) return null;
    return availablePlans.find((p) => p.id === planId);
  }, [availablePlans, planId]);
  const cycle = useMemo(() => {
    if (!plan) return null;
    return (plan.cycles || []).find((c) => c.id === cycleId);
  }, [plan, cycleId]);

  const breadcrumbItems = useMemo(
    () => [
      { label: "Meu perfil", href: "/profile" },
      { label: "Assinatura", href: "/assinatura" },
      { label: "Pagamento", current: true },
    ],
    []
  );

  useEffect(() => {
    if (cycle?.billingType) {
      const candidate = cycle.billingType.toUpperCase();
      if (PAYMENT_METHODS.some((method) => method.value === candidate)) {
        setPaymentMethod(candidate);
      }
    }
    if (cycle?.intervalMonths) {
      setInstallments(Math.min(12, Math.max(1, Number(cycle.intervalMonths))));
    }
  }, [cycle]);

  const planName =
    plan?.name || location.state?.planName || "Plano selecionado";
  const planDescription =
    plan?.description || location.state?.planDescription || "";

  const cycleLabel = cycle
    ? `${intervalLabel(cycle.intervalMonths)} • ${formatCurrency(cycle.price)}`
    : "Ciclo indisponível";

  const handleFormField = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDocumentField = (event) => {
    const masked = formatCpfCnpj(event?.target?.value || "");
    setFormData((prev) => ({ ...prev, document: masked }));
    setDocumentTouched(true);
  };

  const isDocumentValid = useMemo(
    () => isValidDocument(formData.document),
    [formData.document]
  );
  const showDocumentError = documentTouched && !isDocumentValid;

  const canSubmit = () => {
    if (!plan || !cycle) return false;
    if (
      !formData.fullName ||
      !formData.email ||
      !formData.document ||
      !formData.phone ||
      (documentTouched && !isDocumentValid)
    )
      return false;
    if (paymentMethod === "CREDIT_CARD") {
      if (!installments || installments < 1 || installments > 12) {
        return false;
      }
      return (
        formData.cardNumber &&
        formData.cardHolderName &&
        formData.cardExpiryMonth &&
        formData.cardExpiryYear &&
        formData.cardCvv
      );
    }
    return true;
  };

  const handlePaymentSubmit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    setSubmissionError(null);
    try {
      const payload = {
        planId: plan.id,
        cycleId: cycle.id,
        paymentMethod,
        payerName: formData.fullName,
        payerEmail: formData.email,
        payerDocument: formData.document,
        payerPhone: formData.phone,
      };
      if (paymentMethod === "CREDIT_CARD") {
        payload.cardDetails = {
          holderName: formData.cardHolderName,
          number: formData.cardNumber.replace(/\s+/g, ""),
          expiryMonth: formData.cardExpiryMonth,
          expiryYear: formData.cardExpiryYear,
          cvv: formData.cardCvv,
        };
        payload.installmentCount = installments;
      }
      const { data } = await axiosInstance.post(
        "/assinatura/pagamento",
        payload
      );
      setPaymentResult(data?.data ?? null);
    } catch (error) {
      console.error("Erro ao criar pagamento:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível processar o pagamento.";
      setSubmissionError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!planId || !cycleId) {
    return (
      <div className="space-y-6">
        <Alert color="failure">
          Plano ou ciclo não informado. Volte para a página de assinaturas e
          selecione um plano.
        </Alert>
        <Button color="light" onClick={() => navigate("/assinatura")}>
          Voltar para assinaturas
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CorporateBreadcrumb items={breadcrumbItems} />
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
              Assinatura segura
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Confira os dados e finalize o pagamento
            </h1>
            <p className="text-sm text-slate-500">
              {planName} · {cycleLabel}
            </p>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
          Carregando planos e ciclos...
        </div>
      ) : fetchError ? (
        <Alert color="failure" className="rounded-3xl">
          {fetchError}
        </Alert>
      ) : !plan || !cycle ? (
        <Alert color="warning" className="rounded-3xl">
          Não conseguimos encontrar o plano selecionado. Volte e tente
          novamente.
        </Alert>
      ) : (
        <div className="grid gap-6 md:grid-cols-3">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:col-span-2">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                {PAYMENT_METHODS.map((method) => {
                  const IconComponent = method.icon;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => setPaymentMethod(method.value)}
                      className={`flex flex-col gap-2 rounded-2xl border p-4 transition ${
                        paymentMethod === method.value
                          ? "border-orange-500 bg-orange-50"
                          : "border-slate-200 bg-white hover:border-orange-200"
                      }`}
                    >
                      <span
                        className="text-2xl block text-center mx-auto"
                        aria-hidden
                      >
                        {IconComponent ? (
                          <IconComponent className="h-6 w-6 text-orange-500" />
                        ) : null}
                      </span>
                      <p className="text-base font-semibold text-slate-900">
                        {method.title}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                    Nome completo{" "}
                    <span className="text-rose-500" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    value={formData.fullName}
                    onChange={handleFormField("fullName")}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                    E-mail{" "}
                    <span className="text-rose-500" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    type="email"
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    value={formData.email}
                    onChange={handleFormField("email")}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                    CPF/CNPJ{" "}
                    <span className="text-rose-500" aria-hidden>
                      *
                    </span>
                  </label>
                    <input
                      type="text"
                      className={`mt-1 w-full rounded-2xl border px-3 py-2 text-sm text-slate-700 ${
                        showDocumentError
                          ? "border-rose-500 bg-rose-50"
                          : "border-slate-200 bg-slate-50"
                      }`}
                      value={formData.document}
                      onChange={handleDocumentField}
                    />
                    {showDocumentError && (
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-500">
                        CPF ou CNPJ inválido
                      </p>
                    )}
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                    Telefone{" "}
                    <span className="text-rose-500" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    value={formData.phone}
                    onChange={handleFormField("phone")}
                  />
                </div>
              </div>

              {paymentMethod === "CREDIT_CARD" && (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Número do cartão{" "}
                        <span className="text-rose-500" aria-hidden>
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        value={formData.cardNumber}
                        onChange={handleFormField("cardNumber")}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Nome impresso{" "}
                        <span className="text-rose-500" aria-hidden>
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        value={formData.cardHolderName}
                        onChange={handleFormField("cardHolderName")}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Mês de validade{" "}
                        <span className="text-rose-500" aria-hidden>
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="MM"
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        value={formData.cardExpiryMonth}
                        onChange={handleFormField("cardExpiryMonth")}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Ano de validade{" "}
                        <span className="text-rose-500" aria-hidden>
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="AAAA"
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        value={formData.cardExpiryYear}
                        onChange={handleFormField("cardExpiryYear")}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                        CVV{" "}
                        <span className="text-rose-500" aria-hidden>
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        value={formData.cardCvv}
                        onChange={handleFormField("cardCvv")}
                      />
                    </div>
                  </div>
                  <div className="md:flex md:items-center md:gap-3">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 md:w-1/2">
                      Quantidade de parcelas
                    </label>
                    <select
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 md:w-1/2"
                      value={installments}
                      onChange={(event) =>
                        setInstallments(
                          Math.min(12, Math.max(1, Number(event.target.value)))
                        )
                      }
                    >
                      {Array.from({ length: 12 }, (_, index) => index + 1).map(
                        (value) => {
                          const installmentValue =
                            cycle?.price && value > 0
                              ? Number(cycle.price) / value
                              : 0;
                          return (
                            <option key={value} value={value}>
                              {value}x · {formatCurrency(installmentValue)}
                            </option>
                          );
                        }
                      )}
                    </select>
                  </div>
                </div>
              )}

              {submissionError && (
                <Alert color="failure" className="rounded-2xl">
                  {submissionError}
                </Alert>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handlePaymentSubmit}
                  disabled={!canSubmit() || submitting}
                >
                  {submitting
                    ? "Processando pagamento..."
                    : "Confirmar pagamento"}
                </Button>
                {paymentResult && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-600">
                      Pagamento gerado
                    </p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(paymentResult?.value ?? cycle?.price)}
                    </p>
                    <p className="text-sm text-emerald-700">
                      Status: {paymentResult?.status || "pendente"}
                    </p>
                    {paymentResult?.pixUrl && (
                      <a
                        href={paymentResult.pixUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        Abrir link PIX
                      </a>
                    )}
                    {paymentResult?.pixQrCode && (
                      <div className="mt-3 flex flex-col gap-2">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-600">
                          QR Code
                        </span>
                        <img
                          src={`data:image/png;base64,${paymentResult.pixQrCode}`}
                          alt="QR Code PIX"
                          className="h-40 w-40 rounded-2xl border border-white bg-white p-2"
                        />
                      </div>
                    )}
                    {paymentResult?.bankSlipUrl && (
                      <a
                        href={paymentResult.bankSlipUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 underline"
                      >
                        <span>Imprimir boleto</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Plano escolhido
                </p>
                <h2 className="text-2xl font-semibold text-slate-900">
                  {planName}
                </h2>
                {planDescription && (
                  <p className="text-sm text-slate-500">{planDescription}</p>
                )}
              </div>
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Plano selecionado
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {installmentTextForCycle(cycle)}
                </p>
                <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
                  {cycleLabel}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Funcionalidades inclusas
                </p>
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  {(plan.features || []).map((feature) => (
                    <div
                      key={`${plan.id}-${feature}`}
                      className="flex items-start gap-2"
                    >
                      <span className="mt-[2px] h-2 w-2 rounded-full bg-[#e45412]" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPaymentPage;
